import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { fetchCustomers, fetchCustomerStats } from "../store/actions/customerActions";
import { fetchStores } from "../store/actions/storeActions";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card } from "../components/ui/Card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/Table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/Tabs";
import {
  Users,
  Search,
  Filter,
  Download,
  Mail,
  Tag,
  Store,
  MapPin,
  Phone,
  UserPlus,
  Eye,
  ShoppingBag,
} from "lucide-react";
import { useToast } from "../hooks/useToast";
import { formatCurrency } from "../utils/formatCurrency";
import * as ratesService from "../services/ratesService";

const statKeys = [
  { nameKey: "customers.totalCustomers", key: "totalCustomers", icon: Users },
  { nameKey: "customers.active", key: "activeCustomers", icon: Users },
  { nameKey: "customers.avgOrderValue", key: "averageOrderValue", format: "currency", icon: Store },
  { nameKey: "customers.locations", key: "customerLocations", icon: MapPin },
];

const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.heading};
`;

const Sub = styled.p`
  margin: 8px 0 0;
  color: ${({ theme }) => theme.colors.text.body};
`;

const StatGrid = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: 1fr;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const StatCard = styled(Card)`
  padding: 20px;
`;

const StatTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StatName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.body};
`;

const StatValue = styled.div`
  margin-top: 6px;
  font-size: 26px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.heading};
`;

const IconBubble = styled.div`
  border-radius: 9999px;
  padding: 10px;
  background: rgba(139, 92, 246, 0.1);
  color: #8b5cf6;
`;

const TagPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 11px;
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
`;

export default function Customers() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { customers = [], stats: customerStats, loading } = useSelector((state) => state.customer);
  const { stores = [] } = useSelector((state) => state.store);
  const [tab, setTab] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStoreId, setFilterStoreId] = useState("");
  const user = useSelector((state) => state.auth?.user);
  const [currency, setCurrency] = useState(user?.currency || "USD");
  const [rates, setRates] = useState(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    ratesService.getRates().then((r) => setRates(r?.rates || null)).catch(() => setRates(null));
  }, []);

  useEffect(() => {
    dispatch(fetchStores());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchCustomers({ storeId: filterStoreId || undefined }));
    dispatch(fetchCustomerStats(filterStoreId ? { storeId: filterStoreId } : {}));
  }, [dispatch, filterStoreId]);

  const baseCurrency = "USD";
  const stats = statKeys.map((s) => {
    const raw = customerStats != null && s.key in customerStats ? customerStats[s.key] : null;
    let value = loading && customerStats == null ? "…" : raw == null ? "—" : String(raw);
    if (s.format === "currency" && typeof raw === "number") {
      const converted = rates ? ratesService.convert(raw, baseCurrency, currency, rates) : raw;
      value = formatCurrency(converted, currency);
    }
    return { ...s, value };
  });

  const getTagColor = (tag) => {
    const map = {
      VIP: { bg: "#f3e8ff", fg: "#6b21a8" },
      Loyal: { bg: "#dbeafe", fg: "#1d4ed8" },
      "High Value": { bg: "#dcfce7", fg: "#166534" },
    };
    return map[tag] || { bg: "#f3f4f6", fg: "#111827" };
  };

  const filtered = customers.filter((c) =>
    `${c.name || ""} ${c.email || ""}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  function handleNewCustomer() {
    toast({
      title: t("customers.customerAdded"),
      description: t("customers.customerAddedDesc"),
    });
  }

  function handleSendEmail(customerId) {
    toast({
      title: t("customers.emailSent"),
      description: t("customers.emailSentDesc"),
    });
  }

  const fmtAmount = (v) => (v != null && typeof v === "number" ? (rates ? formatCurrency(ratesService.convert(v, baseCurrency, currency, rates), currency) : formatCurrency(v, "USD")) : "—");

  return (
    <div className="space-y-6 animate-fadeIn">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <Title>{t("customers.title")}</Title>
          <Sub>{t("customers.subtitle")}</Sub>
        </div>
        <select value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14 }}>
          <option value="USD">USD</option>
          <option value="EUR">EUR</option>
          <option value="GBP">GBP</option>
        </select>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">{t("customers.overview")}</TabsTrigger>
          <TabsTrigger value="customers">{t("customers.customersTab")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div style={{ marginTop: 24 }} className="space-y-6">
            <StatGrid>
              {stats.map((s) => {
                const Icon = s.icon;
                return (
                  <StatCard key={s.nameKey}>
                    <StatTop>
                      <div>
                        <StatName>{t(s.nameKey)}</StatName>
                        <StatValue>{s.value}</StatValue>
                      </div>
                      <IconBubble>
                        <Icon size={20} />
                      </IconBubble>
                    </StatTop>
                  </StatCard>
                );
              })}
            </StatGrid>

            <div style={{ marginTop: 32, display: "grid", gridTemplateColumns: "1fr", gap: 24 }}>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 24 }}>
                <Card style={{ padding: 20 }}>
                  <h2 style={{ margin: 0, marginBottom: 16, fontSize: 18, fontWeight: 700 }}>{t("customers.topBySpend")}</h2>
                  {(customerStats?.topCustomersBySpent?.length > 0) ? (
                    <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                      {customerStats.topCustomersBySpent.slice(0, 8).map((c) => (
                        <li key={c._id} style={{ padding: "10px 0", borderBottom: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontWeight: 500 }}>{c.name}</span>
                          <span style={{ color: "#6b7280", fontSize: 14 }}>{fmtAmount(c.totalSpent ?? 0)}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>{t("customers.syncToSeeTop")}</p>
                  )}
                </Card>
                <Card style={{ padding: 20 }}>
                  <h2 style={{ margin: 0, marginBottom: 16, fontSize: 18, fontWeight: 700 }}>{t("customers.recentlyAdded")}</h2>
                  {(customerStats?.recentlyAdded?.length > 0) ? (
                    <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                      {customerStats.recentlyAdded.slice(0, 8).map((c) => (
                        <li key={c._id} style={{ padding: "10px 0", borderBottom: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontWeight: 500 }}>{c.name}</span>
                          <span style={{ fontSize: 12, color: "#6b7280" }}>{c.storeId?.name || "—"}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>{t("customers.noCustomersYet")}</p>
                  )}
                </Card>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="customers">
          <div style={{ marginTop: 24 }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div style={{ display: "flex", gap: 12, flex: 1, maxWidth: 520 }}>
                  <select
                    value={filterStoreId}
                    onChange={(e) => setFilterStoreId(e.target.value)}
                    style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14, minWidth: 140 }}
                  >
                    <option value="">{t("customers.allStores")}</option>
                    {stores.map((s) => (
                      <option key={s._id} value={s._id}>{s.name}</option>
                    ))}
                  </select>
                  <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
                    <Search
                      size={16}
                      color="#6b7280"
                      style={{
                        position: "absolute",
                        left: 12,
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    />
                    <Input
                      placeholder={t("customers.searchCustomers")}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{ paddingLeft: 34, width: "100%" }}
                    />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button $variant="outline">
                    <Filter size={14} />
                    {t("customers.filters")}
                  </Button>
                  <Button $variant="outline">
                    <Download size={14} />
                    {t("customers.export")}
                  </Button>
                  <Button onClick={handleNewCustomer}>
                    <UserPlus size={16} />
                    {t("customers.newCustomer")}
                  </Button>
                </div>
              </div>
            </div>

            <div style={{ overflowX: "auto" }}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("customers.customer")}</TableHead>
                    <TableHead>{t("customers.store")}</TableHead>
                    <TableHead>{t("customers.location")}</TableHead>
                    <TableHead>{t("customers.orders")}</TableHead>
                    <TableHead>{t("customers.totalSpent")}</TableHead>
                    <TableHead>{t("customers.lastOrder")}</TableHead>
                    <TableHead>{t("customers.tags")}</TableHead>
                    <TableHead style={{ textAlign: "right" }}>{t("customers.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((customer) => (
                    <TableRow key={customer._id}>
                      <TableCell>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <span style={{ fontWeight: 600 }}>{customer.name}</span>
                          <span style={{ fontSize: 12, color: "#6b7280" }}>
                            <Mail size={12} style={{ marginRight: 4 }} />
                            {customer.email}
                          </span>
                          <span style={{ fontSize: 12, color: "#6b7280" }}>
                            <Phone size={12} style={{ marginRight: 4 }} />
                            {customer.phone}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>{customer.storeId?.name || "—"}</TableCell>
                      <TableCell>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <MapPin size={12} />
                          {customer.location}
                        </span>
                      </TableCell>
                      <TableCell>{customer.orders ?? "—"}</TableCell>
                      <TableCell>{fmtAmount(customer.totalSpent)}</TableCell>
                      <TableCell>{customer.lastOrder ? new Date(customer.lastOrder).toLocaleDateString() : "—"}</TableCell>
                      <TableCell>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {(customer.tags || []).map((tag) => {
                            const c = getTagColor(tag);
                            return (
                              <TagPill key={tag} $bg={c.bg} $fg={c.fg}>
                                <Tag size={11} />
                                {tag}
                              </TagPill>
                            );
                          })}
                        </div>
                      </TableCell>
                      <TableCell style={{ textAlign: "right" }}>
                        <Button
                          $variant="outline"
                          $size="sm"
                          style={{ marginRight: 6 }}
                          onClick={() =>
                            navigate(`/customers/${customer._id}/history`)
                          }
                        >
                          <ShoppingBag size={14} />
                          {t("customers.history")}
                        </Button>
                        <Button
                          $variant="outline"
                          $size="sm"
                          style={{ marginRight: 6 }}
                          onClick={() => handleSendEmail(customer._id)}
                        >
                          <Mail size={14} />
                          {t("customers.email")}
                        </Button>
                        <Button $variant="ghost" $size="icon">
                          <Eye size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

